
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  serverTimestamp,
  DocumentReference,
  FirestoreError,
  writeBatch
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from '@/lib/toast';

export class FirestoreService {
  protected collectionName: string;

  constructor(collectionName: string) {
    this.collectionName = collectionName;
  }

  protected handleError(error: unknown, message: string): never {
    const firestoreError = error as FirestoreError;
    console.error(`${message}:`, firestoreError);
    toast.error(`${message}: ${firestoreError.message}`);
    throw firestoreError;
  }

  protected async getDocById<T>(id: string): Promise<T | null> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const document = await getDoc(docRef);
      
      if (!document.exists()) {
        return null;
      }
      
      return {
        id: document.id,
        ...document.data()
      } as T;
    } catch (error) {
      console.error(`Error fetching document from ${this.collectionName}:`, error);
      return null;
    }
  }

  protected async executeUpdate(
    docId: string, 
    updates: Record<string, any>, 
    errorMessage = 'Failed to update document'
  ): Promise<boolean> {
    try {
      const docRef = doc(db, this.collectionName, docId);
      const docSnapshot = await getDoc(docRef);
      
      if (!docSnapshot.exists()) {
        toast.error('Document not found');
        return false;
      }
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error(errorMessage, firestoreError);
      toast.error(`${errorMessage}: ${firestoreError.message}`);
      return false;
    }
  }

  protected async executeDelete(docId: string, errorMessage = 'Failed to delete document'): Promise<boolean> {
    try {
      await deleteDoc(doc(db, this.collectionName, docId));
      return true;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error(errorMessage, firestoreError);
      toast.error(`${errorMessage}: ${firestoreError.message}`);
      return false;
    }
  }
}
